import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../../context/AuthContext';
import { getRequestCountsByStatus } from '../../../services/serviceRequestService';
import { getCommissionSummary } from '../../../services/commissionService';

const MANAGE_TABS = [
  { label: '고객', path: '/realtor/manage' },
  { label: '추천', path: '/realtor/manage/referral' },
  { label: '정산', path: '/realtor/manage/settlement' },
];

const STATUS_GRID_KEYS = [
  { label: '접수', key: 'unread' },
  { label: '진행중', key: 'consulting' },
  { label: '보류', key: 'pending' },
  { label: '취소', key: 'cancelled' },
  { label: '완료', key: 'completed' },
];

const STATUS_FILTERS = ['대기', '진행', '완료', '취소'];

const LINKS = [
  { label: '고객 목록 · 리드 조회', path: '/realtor/manage/lead' },
  { label: '고객 단체 메시지 발송', path: null },
  { label: '민원 관리', path: '/realtor/manage/complaint' },
];

// ───── 스타일 ─────
const Wrap = styled.div`
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

/* 헤더 (흰 배경, 풀 너비) */
const HeaderArea = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.base};
`;

const HeaderTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const HeaderDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.base};
`;

/* 파란 수익 카드 */
const EarningsCard = styled.div`
  background: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  text-align: left;
`;

const EarningsLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
`;

const EarningsAmount = styled.p`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: #fff;
  margin: 2px 0;
`;

const EarningsSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.7);
`;

/* 탭 */
const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.lg} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
`;

const TabItem = styled.button`
  flex: 1;
  padding: 12px 0;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ $active, theme }) => $active ? theme.fontWeight.semibold : theme.fontWeight.regular};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: -1px;
`;

/* 섹션 */
const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.base};
`;

const SectionDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

/* 상태 그리드 */
const StatusGridWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatusCell = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.sm};
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 64px;
  justify-content: center;
`;

const CellLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const CellValue = styled.span`
  font-size: 20px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

/* 상태 필터 (세그먼트 컨트롤) */
const FilterRow = styled.div`
  display: flex;
  background: #F3F4F6;
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 4px;
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const FilterChip = styled.button`
  flex: 1;
  padding: 8px 0;
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ $active }) => $active ? '#fff' : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${({ $active }) => $active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
`;

/* 링크 리스트 */
const LinkList = styled.div`
  margin: ${({ theme }) => theme.spacing.base} -${({ theme }) => theme.realtor.pagePadding} 0;
  padding: 0 ${({ theme }) => theme.realtor.pagePadding};
  border-top: 6px solid ${({ theme }) => theme.colors.borderLight};
`;

const LinkItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% + ${({ theme }) => theme.realtor.pagePadding} * 2);
  margin-left: -${({ theme }) => theme.realtor.pagePadding};
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.realtor.pagePadding};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: none;
  cursor: pointer;
  text-align: left;

  &:last-child { border-bottom: none; }
`;

const LinkLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  color: ${({ theme }) => theme.colors.text};
`;

const LinkArrow = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// ───── 컴포넌트 ─────
const CustomerTrack = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useAuth();
  const [statusFilter, setStatusFilter] = useState('대기');
  const [counts, setCounts] = useState({});
  const [summary, setSummary] = useState({ withdrawable: 0, total: 0 });

  useEffect(() => {
    if (!userData?.uid) return;
    getRequestCountsByStatus(userData.uid).then(setCounts).catch(() => {});
    getCommissionSummary(userData.uid).then(setSummary).catch(() => {});
  }, [userData?.uid]);

  return (
    <Wrap>
      <HeaderArea>
        <HeaderTitle>관리·정산</HeaderTitle>
        <HeaderDesc>매출과 고객 흐름을 한 눈에 확인하세요</HeaderDesc>
        <EarningsCard>
          <EarningsLabel>출금 가능 수익</EarningsLabel>
          <EarningsAmount>{summary.withdrawable.toLocaleString()}원</EarningsAmount>
          <EarningsSub>총 수수료 {summary.total.toLocaleString()}원</EarningsSub>
        </EarningsCard>
      </HeaderArea>

      <TabRow>
        {MANAGE_TABS.map((t) => (
          <TabItem
            key={t.path}
            $active={location.pathname === t.path}
            onClick={() => navigate(t.path)}
          >
            {t.label}
          </TabItem>
        ))}
      </TabRow>

      <SectionTitle>고객 DB 추적</SectionTitle>
      <SectionDesc>상담부터 완료까지 전환 흐름을 실시간으로 파악합니다.</SectionDesc>

      <StatusGridWrap style={{ gridTemplateColumns: '1fr 1fr' }}>
        {STATUS_GRID_KEYS.slice(0, 2).map((s) => (
          <StatusCell key={s.label}>
            <CellLabel>{s.label}</CellLabel>
            <CellValue>{counts[s.key] || 0}</CellValue>
          </StatusCell>
        ))}
      </StatusGridWrap>
      <StatusGridWrap style={{ marginTop: 8 }}>
        {STATUS_GRID_KEYS.slice(2).map((s) => (
          <StatusCell key={s.label}>
            <CellLabel>{s.label}</CellLabel>
            <CellValue>{counts[s.key] || 0}</CellValue>
          </StatusCell>
        ))}
      </StatusGridWrap>

      <FilterRow>
        {STATUS_FILTERS.map((f) => (
          <FilterChip key={f} $active={statusFilter === f} onClick={() => setStatusFilter(f)}>
            {f}
          </FilterChip>
        ))}
      </FilterRow>

      <LinkList>
        {LINKS.map((l) => (
          <LinkItem
            key={l.label}
            onClick={() => l.path ? navigate(l.path) : alert('준비 중입니다.')}
          >
            <LinkLabel>{l.label}</LinkLabel>
            <LinkArrow>&gt;</LinkArrow>
          </LinkItem>
        ))}
      </LinkList>
    </Wrap>
  );
};

export default CustomerTrack;
